import React, { useState } from 'react';
import { CalendarDaysIcon, ImageIcon, MegaphoneIcon, MousePointerClickIcon, TrendingUpIcon, UsersRoundIcon } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
type Channel = {
  name: string;
  iconUrl?: string;
  spend: string;
  revenue: string;
  roas: string;
  share: number;
};
export const spendByMonth = [{
  label: 'Jan',
  spend: 1850,
  revenue: 8420
}, {
  label: 'Feb',
  spend: 2260,
  revenue: 10180
}, {
  label: 'Mar',
  spend: 1980,
  revenue: 8940
}, {
  label: 'Apr',
  spend: 2540,
  revenue: 12360
}, {
  label: 'May',
  spend: 3120,
  revenue: 15870
}, {
  label: 'Jun',
  spend: 2870,
  revenue: 14210
}];
export const eddyChannels: Channel[] = [{
  name: 'Manymoons',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/1H17upTEPhdYPo8CxCujBt/M_manymoons.svg',
  spend: '$9,480',
  revenue: '$52,140',
  roas: '5.5x',
  share: 45
}, {
  name: 'Vibe ads',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/4QsPQBQikeUopo55GPTpMC/vibe.svg',
  spend: '$5,265',
  revenue: '$21,060',
  roas: '4.0x',
  share: 25
}, {
  name: 'Meta + Instagram',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/3EsJsRbrqwgztzCzWf85MT/meta.svg',
  spend: '$4,212',
  revenue: '$14,320',
  roas: '3.4x',
  share: 20
}, {
  name: 'Email + SMS',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/3rX7mUrbpBP38ioN5pJ69R/Klaviyo.svg',
  spend: '$2,103',
  revenue: '$9,880',
  roas: '4.7x',
  share: 10
}];
export const contentPushes = [{
  month: 'Month 1',
  title: 'Manymoons partnership announcement',
  status: 'Live',
  detail: 'Editorial feature plus paid amplification.'
}, {
  month: 'Month 2',
  title: 'Spring Collection recovery edit',
  status: 'Scheduled',
  detail: 'Jackets and cardigans lead the creative.'
}, {
  month: 'Month 3',
  title: 'Coordinated sets feature',
  status: 'Planned',
  detail: 'Tops and bottoms merchandised as pairs.'
}];
export function MarketingDashboard({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const [period, setPeriod] = useState<'Year to date' | 'Quarter to date'>('Year to date');
  const hasData = selectedOwner === 'Eddy';
  const channels = hasData ? eddyChannels : [];
  const totals = hasData ? {
    spend: '$21,060',
    revenue: '$97,400',
    roas: '4.6x',
    sellThrough: '38%'
  } : {
    spend: '$0',
    revenue: '$0',
    roas: '—',
    sellThrough: '0%'
  };
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="marketing-heading" data-id="element-1520" data-mp="30kdf3i7k7pv3">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-1521" data-mp="2vn3s097n7qaf">
        <div data-id="element-1522" data-mp="2qualpgwj09w8">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-1523" data-mp="2qtitmg250uap">
            <MegaphoneIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1524" data-mp="2hv71uapjbqgo" />
            Marketing
          </span>
          <h1 id="marketing-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-1525" data-mp="2cwdmecqotpdv">
            Marketing performance
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-1526" data-mp="27z3pyd4vsdgj">
            {selectedOwner ? `How Carli is investing marketing dollars to accelerate sell-through for ${selectedOwner}.` : 'Select a brand in the sidebar to see how marketing spend is driving recovery.'}
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-1527" data-mp="11dggzp0cna2p">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-1528" data-mp="4q21bu2irh5q" />
          {selectedOwner || 'All brands'}
        </span>
      </section>

      <section className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Marketing summary" data-id="element-1529" data-mp="dp5key1orwk8">
        <SummaryCard icon={MegaphoneIcon} label="Marketing spend" value={totals.spend} detail="Allocated by Carli" data-id="element-1530" data-mp="ig5ja8ud8opd" />
        <SummaryCard icon={TrendingUpIcon} label="Attributed revenue" value={totals.revenue} detail="From promoted inventory" data-id="element-1531" data-mp="3i22f7db6kvt4" />
        <SummaryCard icon={MousePointerClickIcon} label="Return on ad spend" value={totals.roas} detail="Blended across channels" data-id="element-1532" data-mp="3m35r5oq3y7p2" />
        <SummaryCard icon={UsersRoundIcon} label="Promoted sell-through" value={totals.sellThrough} detail="Units sold from campaigns" data-id="element-1533" data-mp="2y10vi1xf49n2" />
      </section>

      <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-label="Spend versus attributed revenue" data-id="element-1534" data-mp="3cqow10xrz49q">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-id="element-1535" data-mp="37xcbp304pjms">
          <div data-id="element-1536" data-mp="3lsen25nqw16z">
            <h2 className="text-lg font-semibold" data-id="element-1537" data-mp="3grqxzpj2tufe">Spend vs. attributed revenue</h2>
            <p className="mt-1 text-sm text-ink/60" data-id="element-1538" data-mp="3gtuxzwduu9ae">Marketing investment measured against recovered revenue.</p>
          </div>
          <div className="flex gap-1 rounded-xl border border-ink/10 bg-cream p-1" data-id="element-1539" data-mp="3qp53iu3goysx">
            {(['Quarter to date', 'Year to date'] as const).map(item => <button key={item} type="button" onClick={() => setPeriod(item)} aria-pressed={period === item} className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${period === item ? 'bg-[#BB5E40] text-cream' : 'text-ink/60 hover:text-ink'}`} data-id="element-1540" data-mp="21culezhq8p1e">
                {item}
              </button>)}
          </div>
        </div>
        <div className="mt-6 h-64 sm:h-72" data-id="element-1541" data-mp="711uk4zcme0h">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1542" data-mp="3p8rbw2n5htpg">
            <BarChart data={period === 'Year to date' ? spendByMonth : spendByMonth.slice(-3)} margin={{
            top: 8,
            right: 8,
            left: -15,
            bottom: 0
          }} barCategoryGap="22%" barGap={6} data-id="element-1543" data-mp="3palvndmorfry">
              <CartesianGrid vertical={false} stroke="#1C1A17" strokeOpacity={0.1} strokeDasharray="3 5" data-id="element-1544" data-mp="3fbwb1d3enh80" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{
              fill: '#6D6861',
              fontSize: 12
            }} dy={8} data-id="element-1545" data-mp="3k9of15hbqtsk" />
              <YAxis axisLine={false} tickLine={false} tick={{
              fill: '#6D6861',
              fontSize: 12
            }} tickFormatter={(value: number) => `$${Math.round(value / 1000)}k`} data-id="element-1546" data-mp="1dh5i6ktit2o1" />
              <Tooltip content={<ChartTooltip data-id="element-1548" data-mp="1h27atj7kc01w" />} cursor={{
              fill: '#BB5E40',
              fillOpacity: 0.06
            }} data-id="element-1547" data-mp="1h1nu25tnfhpg" />
              <Bar dataKey="spend" name="Spend" fill="#BB5E40" radius={[6, 6, 0, 0]} maxBarSize={64} data-id="element-1549" data-mp="1c0zyd6uh6cow" />
              <Bar dataKey="revenue" name="Attributed revenue" fill="#4F8A5B" radius={[6, 6, 0, 0]} maxBarSize={64} data-id="element-1550" data-mp="17389ymt1h8pg" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-ink/60" data-id="element-1551" data-mp="1zqcbghsxb4wh">
          <span className="inline-flex items-center gap-2" data-id="element-1552" data-mp="1urr7q8egq33a"><span className="h-2.5 w-2.5 rounded-full bg-[#BB5E40]" data-id="element-1553" data-mp="1ut2mf39g4v96" />Marketing spend</span>
          <span className="inline-flex items-center gap-2" data-id="element-1554" data-mp="1pzzpqm9dh5or"><span className="h-2.5 w-2.5 rounded-full bg-[#4F8A5B]" data-id="element-1555" data-mp="1l2r60h8kstle" />Attributed revenue</span>
        </div>
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-[1.15fr_0.85fr]" data-id="element-1556" data-mp="2ii50600ym3ye">
        <article className="min-w-0 rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="marketing-channels-heading" data-id="element-1557" data-mp="2dr52utnxhjvd">
          <div className="flex items-center gap-2" data-id="element-1558" data-mp="2m0ueqwcd7b4h">
            <MegaphoneIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1559" data-mp="2q1eb3xp46apk" />
            <h2 id="marketing-channels-heading" className="text-base font-semibold" data-id="element-1560" data-mp="2q3h0cfifzzt8">Spend by channel</h2>
          </div>
          {channels.length > 0 ? <div className="mt-5 -mx-2 overflow-x-auto px-2" data-id="element-1561" data-mp="300lmwgg79xak">
              <table className="w-full min-w-[520px] text-left text-xs" data-id="element-1562" data-mp="34yn5z1dmlwm1">
                <thead className="border-b border-ink/10 text-ink/50" data-id="element-1563" data-mp="34wkjgrbomlm2">
                  <tr data-id="element-1564" data-mp="38yxmh7z1o5f5">
                    <th scope="col" className="pb-2 font-semibold" data-id="element-1565" data-mp="38y5qvx5g67ov">Channel</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1566" data-mp="3drieq52ayagk">Spend</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1567" data-mp="3ip0gnbhwjpf2">Revenue</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1568" data-mp="3nq7m33wkocik">ROAS</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1569" data-mp="3snpumt6ogpq1">% of spend</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink/10" data-id="element-1570" data-mp="hs8set84p2jg">
                  {channels.map(channel => <tr key={channel.name} data-id="element-1571" data-mp="cufpc924gone">
                      <th scope="row" className="py-3 text-left font-medium text-ink" data-id="element-1572" data-mp="qv07d307qgfu">
                        <span className="flex items-center gap-2.5" data-id="element-1573" data-mp="lvn9ln7szoyh">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#BB5E40] p-1.5" data-id="element-1574" data-mp="ltat7ehp1mnj">
                            {channel.iconUrl ? <img src={channel.iconUrl} alt="" className="h-full w-full object-contain" data-id="element-1575" data-mp="3qb6lasq2wg29" /> : <MegaphoneIcon className="h-4 w-4 text-cream" aria-hidden="true" data-id="element-1576" data-mp="34w3cuj44eob" />}
                          </span>
                          {channel.name}
                        </span>
                      </th>
                      <td className="py-3 text-right font-semibold tabular-nums text-ink" data-id="element-1577" data-mp="36kuq8y2aamgm">{channel.spend}</td>
                      <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1578" data-mp="1m2ajtpn4p3cx">{channel.revenue}</td>
                      <td className="py-3 text-right tabular-nums text-forest" data-id="element-1579" data-mp="1qvlgixy883r4">{channel.roas}</td>
                      <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1580" data-mp="1d4qgmb5pgwzo">{channel.share}%</td>
                    </tr>)}
                </tbody>
              </table>
            </div> : <p className="py-8 text-center text-sm text-ink/55" data-id="element-1581" data-mp="25pqd1kvyfo4u">Select a brand to view marketing spend by channel.</p>}
        </article>

        <article className="rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="content-calendar-heading" data-id="element-1582" data-mp="1vsbwlla16zcj">
          <div className="flex items-center gap-2" data-id="element-1583" data-mp="20qx0buohs15q">
            <CalendarDaysIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1584" data-mp="kdoyc16pulbx" />
            <h2 id="content-calendar-heading" className="text-base font-semibold" data-id="element-1585" data-mp="pd1iufa47gbc">Content pushes</h2>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-ink/55" data-id="element-1586" data-mp="t4p5edbckxcl">Monthly partnership promotion for the first three months.</p>
          <ol className="mt-5 space-y-3" data-id="element-1587" data-mp="y30eglttj3tr">
            {contentPushes.map(push => <li key={push.title} className="rounded-xl border border-ink/10 bg-cream/45 p-4" data-id="element-1588" data-mp="jee0anfxgi44">
                <div className="flex items-center justify-between gap-3" data-id="element-1589" data-mp="odgtnamjozw9">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/45" data-id="element-1590" data-mp="aezcjvr5alnb">{push.month}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${push.status === 'Live' ? 'bg-forest/10 text-forest' : push.status === 'Scheduled' ? 'bg-[#C9A04E]/15 text-[#8B6A20]' : 'bg-ink/7 text-ink/55'}`} data-id="element-1591" data-mp="egkuhvijdq4d">{push.status}</span>
                </div>
                <p className="mt-2 text-sm font-semibold" data-id="element-1592" data-mp="1uvm4b7mnvw7r">{push.title}</p>
                <p className="mt-1 text-xs leading-relaxed text-ink/55" data-id="element-1593" data-mp="1zu75b99qmjx9">{push.detail}</p>
              </li>)}
          </ol>
          <div className="mt-4 flex items-start gap-2 rounded-xl bg-[#BB5E40]/5 p-3" data-id="element-1594" data-mp="3kchjsokaflsm">
            <ImageIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-1595" data-mp="3fdo72yctryti" />
            <p className="text-xs leading-relaxed text-ink/60" data-id="element-1596" data-mp="3u2knbip1ci57">Request product placement in Manymoons’ Vibe ads alongside each monthly push.</p>
          </div>
        </article>
      </section>
    </div>;
}
function SummaryCard({
  icon: Icon,
  label,
  value,
  detail
}: {
  icon: typeof MegaphoneIcon;
  label: string;
  value: string;
  detail: string;
}) {
  return <article className="rounded-2xl border border-ink/10 bg-paper p-5" data-id="element-1597" data-mp="guprf928oztw">
      <Icon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1598" data-mp="gsn1ep1376p6" />
      <p className="mt-4 text-sm text-ink/55" data-id="element-1599" data-mp="bwfsqohb3h3j">{label}</p>
      <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-1600" data-mp="2im2v5ush11od">{value}</p>
      <p className="mt-1 text-xs text-ink/50" data-id="element-1601" data-mp="2dnj4jh2xn1po">{detail}</p>
    </article>;
}
function ChartTooltip({
  active,
  payload,
  label
}: {
  active?: boolean;
  payload?: Array<{
    value?: number;
    name?: string;
    color?: string;
  }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return <div className="rounded-xl border border-ink/10 bg-paper px-3 py-2 shadow-lg" data-id="element-1602" data-mp="3cbjyqtdu4v88">
      <p className="text-xs text-ink/50" data-id="element-1603" data-mp="3hb6a0xa3s1ee">{label}</p>
      {payload.map(entry => <p key={entry.name} className="mt-1 flex items-center gap-2 text-sm font-semibold tabular-nums text-ink" data-id="element-1604" data-mp="3h8to691856w2">
          <span className="h-2 w-2 rounded-full" style={{
        backgroundColor: entry.color
      }} data-id="element-1605" data-mp="3qbmdw7urdajs" />
          {entry.name}: {(entry.value ?? 0).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
      })}
        </p>)}
    </div>;
}